import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Plus, Trash2, Play, TrendingUp, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { calcSensitivityScenario, sensitivityValues } from '@/utils/equityMath';
import type { SensitivityShareholder, PoolTiming } from '@/utils/equityMath';

interface Shareholder extends SensitivityShareholder {
  currentOwnership: number;
}

/** The round every sweep varies one dimension of. */
interface BaseRound {
  preMoneyValuation: number;
  newMoney: number;
  poolRefreshPct: number;
  /** Defaults to 'pre', which is what an unqualified pool percentage means. */
  poolTiming: PoolTiming;
}

interface Variable {
  id: string;
  name: string;
  type: 'valuation' | 'dilution' | 'optionPool';
  minValue: number;
  maxValue: number;
  steps: number;
}

interface ScenarioResult {
  scenarioName: string;
  valuation: number;
  dilution: number;
  optionPool: number;
  pricePerShare: number;
  shareholders: {
    id: string;
    name: string;
    ownership: number;
    shares: number;
  }[];
  totalShares: number;
}

export default function CapTableSensitivityAnalyzer() {
  const [shareholders, setShareholders] = useState<Shareholder[]>([
    { id: '1', name: 'Founder 1', currentShares: 5000000, currentOwnership: 40 },
    { id: '2', name: 'Founder 2', currentShares: 3750000, currentOwnership: 30 },
    { id: '3', name: 'Angel Investors', currentShares: 2500000, currentOwnership: 20 },
    {
      id: '4',
      name: 'Option Pool',
      currentShares: 1250000,
      currentOwnership: 10,
      isOptionPool: true,
    },
  ]);

  // The round the sweeps vary. Without a round size a valuation means nothing,
  // which is why the valuation variable used to have no effect on anything.
  const [baseRound, setBaseRound] = useState<BaseRound>({
    preMoneyValuation: 10000000,
    newMoney: 2000000,
    poolRefreshPct: 10,
    poolTiming: 'pre',
  });
  const [scenarioWarnings, setScenarioWarnings] = useState<string[]>([]);

  const [variables, setVariables] = useState<Variable[]>([
    {
      id: '1',
      name: 'Pre-Money Valuation',
      type: 'valuation',
      minValue: 8000000,
      maxValue: 15000000,
      steps: 8,
    },
    { id: '2', name: 'New Money Dilution', type: 'dilution', minValue: 15, maxValue: 30, steps: 6 },
    {
      id: '3',
      name: 'Option Pool Refresh',
      type: 'optionPool',
      minValue: 5,
      maxValue: 20,
      steps: 4,
    },
  ]);

  const [scenarios, setScenarios] = useState<ScenarioResult[]>([]);
  const [analyzing, setAnalyzing] = useState(false);

  const addShareholder = () => {
    const newId = String(Date.now());
    setShareholders([
      ...shareholders,
      { id: newId, name: '', currentShares: 0, currentOwnership: 0 },
    ]);
  };

  const updateShareholder = (
    id: string,
    field: keyof Shareholder,
    value: string | number | boolean
  ) => {
    setShareholders(
      shareholders.map((sh) => {
        if (sh.id === id) {
          const updated = { ...sh, [field]: value };
          // Recalculate shares or ownership based on which was changed
          const totalShares = shareholders.reduce(
            (sum, s) => sum + (s.id === id ? 0 : s.currentShares),
            0
          );
          if (field === 'currentOwnership') {
            updated.currentShares = Math.round(
              ((Number(value) / 100) * totalShares) / (1 - Number(value) / 100)
            );
          }
          return updated;
        }
        return sh;
      })
    );
  };

  const removeShareholder = (id: string) => {
    setShareholders(shareholders.filter((sh) => sh.id !== id));
  };

  const updateVariable = (id: string, field: keyof Variable, value: string | number) => {
    setVariables(variables.map((v) => (v.id === id ? { ...v, [field]: value } : v)));
  };

  const addVariable = () => {
    const newId = String(Date.now());
    setVariables([
      ...variables,
      { id: newId, name: 'New Variable', type: 'dilution', minValue: 0, maxValue: 50, steps: 5 },
    ]);
  };

  const removeVariable = (id: string) => {
    setVariables(variables.filter((v) => v.id !== id));
  };

  const runAnalysis = () => {
    setAnalyzing(true);
    setTimeout(() => {
      const results: ScenarioResult[] = [];
      const warnings = new Set<string>();

      variables.forEach((variable) => {
        for (const value of sensitivityValues(
          variable.minValue,
          variable.maxValue,
          variable.steps
        )) {
          // Everything the variable is not sweeping is held at the base round.
          let preMoneyValuation = baseRound.preMoneyValuation;
          const newMoney = baseRound.newMoney;
          let poolRefreshPct = baseRound.poolRefreshPct;

          if (variable.type === 'valuation') {
            preMoneyValuation = value;
          } else if (variable.type === 'dilution') {
            // A dilution sweep is a statement about how much of the company is
            // sold, so it fixes the money and implies the valuation that buys it.
            const pct = Math.min(99, Math.max(0, value));
            preMoneyValuation = pct > 0 ? (newMoney * (100 - pct)) / pct : preMoneyValuation;
          } else if (variable.type === 'optionPool') {
            poolRefreshPct = value;
          }

          const scenario = calcSensitivityScenario(shareholders, {
            preMoneyValuation,
            newMoney,
            poolRefreshPct,
            // Was never passed, so every sweep silently modelled a post-money
            // pool — the founder-friendly variant — and reported founders
            // keeping about two points more than a standard term leaves them.
            poolTiming: baseRound.poolTiming,
          });
          scenario.warnings.forEach((w) => warnings.add(w));

          results.push({
            scenarioName: `${variable.name}: ${
              variable.type === 'valuation'
                ? '$' + (value / 1000000).toFixed(1) + 'M'
                : value.toFixed(1) + '%'
            }`,
            valuation: scenario.preMoneyValuation,
            dilution: scenario.dilutionPct,
            optionPool: scenario.poolRefreshPct,
            pricePerShare: scenario.pricePerShare,
            shareholders: scenario.shareholders,
            totalShares: scenario.totalShares,
          });
        }
      });

      setScenarioWarnings([...warnings]);
      setScenarios(results);
      setAnalyzing(false);
    }, 800);
  };

  // Prepare chart data
  const ownershipTrendData = scenarios.map((scenario) => {
    const data: Record<string, string | number> = { scenario: scenario.scenarioName };
    scenario.shareholders.forEach((sh) => {
      data[`sh_${sh.id}`] = Number(sh.ownership.toFixed(2));
    });
    return data;
  });

  const founderImpactData = scenarios.map((scenario) => {
    const founders = scenario.shareholders.filter((sh) => sh.name.includes('Founder'));
    const totalFounderOwnership = founders.reduce((sum, f) => sum + f.ownership, 0);
    return {
      scenario: scenario.scenarioName,
      founderOwnership: totalFounderOwnership.toFixed(2),
      dilution: (100 - totalFounderOwnership).toFixed(2),
    };
  });

  const sensitivityMatrix =
    scenarios.length > 0
      ? shareholders.map((sh) => {
          const ownerships = scenarios.map((s) => {
            const shareholder = s.shareholders.find((shh) => shh.name === sh.name);
            return shareholder ? shareholder.ownership : 0;
          });
          const minOwnership = Math.min(...ownerships);
          const maxOwnership = Math.max(...ownerships);
          const variance = maxOwnership - minOwnership;

          return {
            shareholder: sh.name,
            minOwnership: minOwnership.toFixed(2),
            maxOwnership: maxOwnership.toFixed(2),
            variance: variance.toFixed(2),
            risk: variance > 10 ? 'High' : variance > 5 ? 'Medium' : 'Low',
          };
        })
      : [];

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Cap Table Sensitivity Analyzer
          </CardTitle>
          <CardDescription>
            Model how valuation, dilution, and option pool variables affect ownership across
            scenarios
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Cap Table */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Current Cap Table</h3>
              <Button onClick={addShareholder} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Shareholder
              </Button>
            </div>
            <div className="space-y-2">
              {shareholders.map((sh) => (
                <div key={sh.id} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4">
                    <Input
                      placeholder="Shareholder name"
                      value={sh.name}
                      onChange={(e) => updateShareholder(sh.id, 'name', e.target.value)}
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      placeholder="Shares"
                      value={sh.currentShares || ''}
                      onChange={(e) =>
                        updateShareholder(sh.id, 'currentShares', Number(e.target.value))
                      }
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      placeholder="Ownership %"
                      value={sh.currentOwnership || ''}
                      onChange={(e) =>
                        updateShareholder(sh.id, 'currentOwnership', Number(e.target.value))
                      }
                    />
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={sh.isOptionPool ?? false}
                      onChange={(e) => updateShareholder(sh.id, 'isOptionPool', e.target.checked)}
                      title="This line is the option pool that a refresh tops up"
                      aria-label={`${sh.name || 'Shareholder'} is the option pool`}
                    />
                  </div>
                  <div className="col-span-1">
                    <Button onClick={() => removeShareholder(sh.id)} size="sm" variant="ghost">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">
                Tick the line that is the option pool — a refresh tops that line up rather than
                matching on whether the name happens to contain the word &ldquo;Option&rdquo;.
              </p>
            </div>
          </div>

          {/* Variables to Analyze */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Variables to Analyze</h3>
              <Button onClick={addVariable} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Variable
              </Button>
            </div>
            <div className="space-y-2">
              {variables.map((variable) => (
                <div
                  key={variable.id}
                  className="grid grid-cols-12 gap-2 items-center p-3 border rounded-lg"
                >
                  <div className="col-span-3">
                    <Label className="text-xs">Variable Name</Label>
                    <Input
                      value={variable.name}
                      onChange={(e) => updateVariable(variable.id, 'name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Type</Label>
                    <select
                      value={variable.type}
                      onChange={(e) => updateVariable(variable.id, 'type', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    >
                      <option value="valuation">Valuation</option>
                      <option value="dilution">Dilution %</option>
                      <option value="optionPool">Option Pool %</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Min Value</Label>
                    <Input
                      type="number"
                      value={variable.minValue}
                      onChange={(e) =>
                        updateVariable(variable.id, 'minValue', Number(e.target.value))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Max Value</Label>
                    <Input
                      type="number"
                      value={variable.maxValue}
                      onChange={(e) =>
                        updateVariable(variable.id, 'maxValue', Number(e.target.value))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Steps</Label>
                    <Input
                      type="number"
                      value={variable.steps}
                      onChange={(e) => updateVariable(variable.id, 'steps', Number(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button onClick={() => removeVariable(variable.id)} size="sm" variant="ghost">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Base Round */}
          <div>
            <h3 className="font-semibold mb-1">Base Round</h3>
            <p className="text-xs text-muted-foreground mb-3">
              The round each sweep varies one dimension of. A valuation only means something
              alongside an amount of money, so the dilution is derived from these two rather than
              set separately.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Pre-Money Valuation ($)</Label>
                <Input
                  type="number"
                  min="0"
                  value={baseRound.preMoneyValuation || ''}
                  onChange={(e) =>
                    setBaseRound({
                      ...baseRound,
                      preMoneyValuation: Math.max(0, Number(e.target.value)),
                    })
                  }
                />
              </div>
              <div>
                <Label className="text-xs">New Money Raised ($)</Label>
                <Input
                  type="number"
                  min="0"
                  value={baseRound.newMoney || ''}
                  onChange={(e) =>
                    setBaseRound({ ...baseRound, newMoney: Math.max(0, Number(e.target.value)) })
                  }
                />
              </div>
              <div>
                <Label className="text-xs">Option Pool Refresh (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="99"
                  value={baseRound.poolRefreshPct || ''}
                  onChange={(e) =>
                    setBaseRound({
                      ...baseRound,
                      poolRefreshPct: Math.min(99, Math.max(0, Number(e.target.value))),
                    })
                  }
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  As a share of the company <em>after</em> the round.
                </p>
              </div>
              {baseRound.poolRefreshPct > 0 && (
                <div>
                  <Label className="text-xs">Pool created</Label>
                  <select
                    value={baseRound.poolTiming}
                    onChange={(e) =>
                      setBaseRound({ ...baseRound, poolTiming: e.target.value as PoolTiming })
                    }
                    className="w-full mt-1 h-10 px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="pre">Before the round — you pay for it</option>
                    <option value="post">After the round — everyone pays</option>
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {baseRound.poolTiming === 'pre'
                      ? 'The usual term: existing holders carry the whole pool.'
                      : 'Better for you, and less common — check the term sheet says so.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {scenarioWarnings.length > 0 && (
            <div className="space-y-2">
              {scenarioWarnings.map((warning, i) => (
                <div
                  key={i}
                  className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
                >
                  <p className="text-sm text-muted-foreground">{warning}</p>
                </div>
              ))}
            </div>
          )}

          <Button onClick={runAnalysis} disabled={analyzing} className="w-full">
            <Play className="h-4 w-4 mr-2" />
            {analyzing ? 'Analyzing...' : 'Run Sensitivity Analysis'}
          </Button>
        </CardContent>
      </Card>

      {scenarios.length > 0 && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Sensitivity Analysis Results</CardTitle>
              <CardDescription>
                {scenarios.length} scenarios analyzed across {variables.length} variables
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="ownership">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="ownership">Ownership Trends</TabsTrigger>
                  <TabsTrigger value="founder">Founder Impact</TabsTrigger>
                  <TabsTrigger value="matrix">Sensitivity Matrix</TabsTrigger>
                </TabsList>

                <TabsContent value="ownership" className="space-y-4">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={ownershipTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="scenario" angle={-45} textAnchor="end" height={100} />
                      <YAxis label={{ value: 'Ownership %', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      {shareholders.map((sh, idx) => (
                        <Line
                          key={sh.id}
                          type="monotone"
                          dataKey={`sh_${sh.id}`}
                          name={sh.name}
                          stroke={COLORS[idx % COLORS.length]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </TabsContent>

                <TabsContent value="founder" className="space-y-4">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={founderImpactData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="scenario" angle={-45} textAnchor="end" height={100} />
                      <YAxis label={{ value: 'Percentage', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="founderOwnership"
                        fill="hsl(var(--chart-2))"
                        name="Total Founder Ownership %"
                      />
                      <Bar dataKey="dilution" fill="hsl(var(--chart-5))" name="Total Dilution %" />
                    </BarChart>
                  </ResponsiveContainer>
                </TabsContent>

                <TabsContent value="matrix" className="space-y-4">
                  <div className="rounded-lg border">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Shareholder</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">
                            Min Ownership %
                          </th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">
                            Max Ownership %
                          </th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Variance</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">
                            Risk Level
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {sensitivityMatrix.map((row, idx) => (
                          <tr key={idx} className="hover:bg-muted/30">
                            <td className="px-4 py-3 font-medium">{row.shareholder}</td>
                            <td className="px-4 py-3 text-right">{row.minOwnership}%</td>
                            <td className="px-4 py-3 text-right">{row.maxOwnership}%</td>
                            <td className="px-4 py-3 text-right font-semibold">{row.variance}%</td>
                            <td className="px-4 py-3 text-center">
                              <Badge
                                variant={
                                  row.risk === 'High'
                                    ? 'destructive'
                                    : row.risk === 'Medium'
                                      ? 'default'
                                      : 'secondary'
                                }
                              >
                                {row.risk}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                    <h4 className="font-semibold flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Key Insights
                    </h4>
                    <ul className="space-y-1 text-sm text-muted-foreground ml-6">
                      {sensitivityMatrix
                        .filter((row) => Number(row.variance) > 5)
                        .map((row, idx) => (
                          <li key={idx}>
                            <strong>{row.shareholder}</strong> shows high sensitivity with{' '}
                            {row.variance}% variance in ownership across scenarios
                          </li>
                        ))}
                      {sensitivityMatrix.every((row) => Number(row.variance) <= 5) && (
                        <li>
                          All shareholders show low sensitivity to the analyzed variables (variance{' '}
                          {'<'} 5%)
                        </li>
                      )}
                    </ul>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
